import React from 'react';
import DOMPurify from 'dompurify';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Trash2, Edit, Send, Filter, CreditCard, Lock, Unlock, CalendarClock, Search } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import MessageFiles from '../../components/MessageFiles';
import LessonFiles from '../../components/LessonFiles';
import RichTextEditor from '../../components/RichTextEditor';
import FilePickerInput from '../../components/FilePickerInput';
import PasswordInput from '../../components/PasswordInput';
import YouTubeEmbed from '../../components/YouTubeEmbed';

const getFullName = (user) => {
  if (!user) return '';
  const full = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return full || user.username || '';
};

export default function ClassesTab({ ctx }) {
  const {
    token,
    classes,
    users,
    students,
    teachers,
    filteredStudents,
    filteredTeachers,
    newUser,
    setNewUser,
    createStudentDialogOpen,
    setCreateStudentDialogOpen,
    createTeacherDialogOpen,
    setCreateTeacherDialogOpen,
    handleOpenCreateStudent,
    handleOpenCreateTeacher,
    handleCreateUser,
    handleEditUser,
    handleDeleteUser,
    handleUnlockUser,
    toggleClassSelection,
    studentSearch,
    setStudentSearch,
    teacherSearch,
    setTeacherSearch,
    newClassName,
    setNewClassName,
    classDialogOpen,
    setClassDialogOpen,
    handleCreateClass,
    handleDeleteClass,
    setNewYearWizardOpen,
    lessons,
    lessonClassFilter,
    setLessonClassFilter,
    handleEditLesson,
    handleDeleteLesson,
    boardContent,
    setBoardContent,
    boardClassId,
    setBoardClassId,
    boardFiles,
    setBoardFiles,
    handleSendBoardMessage,
    privateContent,
    setPrivateContent,
    privateRecipient,
    setPrivateRecipient,
    privateFiles,
    setPrivateFiles,
    handleSendPrivateMessage,
    recipients,
    boardMessages,
    privateMessages,
    boardFilter,
    setBoardFilter,
    privateFilter,
    setPrivateFilter,
    messageSearch,
    setMessageSearch,
    getSenderRoleColor,
    handleEditMessage,
    handleDeleteMessage,
    payments,
    paymentDialogOpen,
    setPaymentDialogOpen,
    handleUpdatePayment,
    paymentStudent,
    setPaymentStudent,
    paymentMonth,
    setPaymentMonth,
    paymentYear,
    setPaymentYear,
    paymentStatus,
    setPaymentStatus,
  } = ctx;
  return (
          <TabsContent value="classes" data-testid="classes-content">
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="text-xl font-semibold text-slate-900">Управление на класове</h2>
                <div className="flex gap-2">
                  <Button
                    data-testid="new-school-year-btn"
                    onClick={() => setNewYearWizardOpen(true)}
                    variant="outline"
                    className="border-[#00966E] text-[#00966E] hover:bg-green-50 flex items-center gap-2"
                  >
                    <CalendarClock className="h-4 w-4" />
                    Нова учебна година
                  </Button>
                  <Dialog open={classDialogOpen} onOpenChange={setClassDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        data-testid="add-class-btn"
                        className="bg-[#00966E] hover:bg-[#007a59] text-white flex items-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Добави клас
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Добавяне на клас</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateClass} className="space-y-4">
                        <div>
                          <Label htmlFor="className">Име на класа</Label>
                          <Input
                            id="className"
                            data-testid="class-name-input"
                            value={newClassName}
                            onChange={(e) => setNewClassName(e.target.value)}
                            placeholder="напр. 1-ви А клас"
                            required
                          />
                        </div>
                        <Button type="submit" data-testid="submit-class-btn" className="w-full bg-[#00966E] hover:bg-[#007a59] text-white">
                          Добави
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {classes.map((cls) => {
                  const classTeachers = users.filter(u => u.role === 'teacher' && u.class_ids?.includes(cls.class_id));
                  const classStudents = users.filter(u => u.role === 'student' && u.class_ids?.includes(cls.class_id));
                  return (
                    <Card key={cls.class_id} data-testid="class-card" className="p-4 border border-slate-200">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-slate-900 text-lg">{cls.name}</h3>
                          {cls.school_year && (
                            <Badge className="mt-1 text-xs bg-amber-100 text-amber-700 font-normal">
                              {cls.school_year}
                            </Badge>
                          )}
                        </div>
                        <Button
                          onClick={() => handleDeleteClass(cls.class_id)}
                          variant="ghost"
                          size="sm"
                          data-testid="delete-class-btn"
                          className="text-[#D01C1F] hover:text-[#a81619] hover:bg-red-50 flex-shrink-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Teachers */}
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                          Учители ({classTeachers.length})
                        </p>
                        {classTeachers.length === 0 ? (
                          <p className="text-xs text-slate-400 italic">Няма назначени учители</p>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {classTeachers.map(t => (
                              <Badge key={t.user_id} className="text-xs bg-blue-100 text-blue-700 font-normal">
                                {getFullName(t)}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Students */}
                      <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                          Ученици ({classStudents.length})
                        </p>
                        {classStudents.length === 0 ? (
                          <p className="text-xs text-slate-400 italic">Няма записани ученици</p>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {classStudents.map(s => (
                              <Badge key={s.user_id} className="text-xs bg-green-100 text-green-700 font-normal">
                                {getFullName(s)}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          </TabsContent>
  );
}
